import { useQuery } from "react-query";
import { useEffect, useRef, useState } from "react";

import { InformationAfterSignup } from "doxecure-commons/doxecureTypes";
import { $ok, $totype, $UUID } from "foundation-ts/commons";
import { TSDate } from "foundation-ts/tsdate";
import { Resp } from "foundation-ts/tsrequest";
import { Nullable } from "foundation-ts/types";

import apiClient from "@services/apiClient";

import { InfosAfterSignupID } from "./hooksConstants";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 10000;

const useFetchInfosAfterSignup = (corporationId: Nullable<string>) => {
    const [remainingSeconds, setRemainingSeconds] = useState(POLL_TIMEOUT_MS / 1000);
    const [isTimedOut, setIsTimedOut] = useState(false);
    const pollStartTime = useRef<number|null>(null);
    const isResolvedRef = useRef(false);

    const query = useQuery(
        [InfosAfterSignupID, corporationId],
        async () => {
            const [resp, status] = await apiClient.getInfosAfterSignup($UUID(corporationId)!);
            if (status !== Resp.OK) { return null; }
            const data = $totype<InformationAfterSignup>(resp);
            isResolvedRef.current = ($ok(data?.order?.status) && ["paid", "canceled", "failed", "expired"].includes(data!.order!.status));
            return data;
        },
        {
            enabled: Boolean(corporationId),
            cacheTime: 0,
            staleTime: 0,
            refetchInterval: (data) => {
                if ($ok(data?.order?.status) && ["paid", "canceled", "failed", "expired"].includes(data!.order!.status)) { 
                    isResolvedRef.current = true;
                    return false;
                }

                if (isTimedOut) { return false; }

                return POLL_INTERVAL_MS;
            },
            refetchIntervalInBackground: false
        }
    );

    useEffect(() => {
        if (!$ok(corporationId)) { return; }

        isResolvedRef.current = false;
        pollStartTime.current = TSDate.zulu().timestamp;
        setRemainingSeconds(POLL_TIMEOUT_MS / 1000);
        setIsTimedOut(false);

        const timer = setInterval(() => {
            if (isResolvedRef.current) {
                clearInterval(timer);
                return;
            }

            const elapsed = TSDate.zulu().timestamp - pollStartTime.current!;
            const remaining = Math.max(0, POLL_TIMEOUT_MS/1000 - elapsed);
            setRemainingSeconds(remaining);

            if (remaining === 0) {
                setIsTimedOut(true);
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [corporationId]);

    return {
        ...query,
        remainingSeconds,
        isResolved: isResolvedRef.current,
        isTimedOut,
    };
};

export default useFetchInfosAfterSignup ;